import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { MetricDetailTable } from "./MetricDetailTable";
import { useMetricsDetails } from "@/hooks/useMetricsDetails";
import { Skeleton } from "@/components/ui/skeleton";

interface MetricDetails {
  title: string;
  value: string;
  change?: number;
  trend?: 'up' | 'down' | 'neutral';
  metricType: 'mrr' | 'arr' | 'churn' | 'revenue' | 'cac' | 'ltv' | 'average_ticket';
  filters: {
    productId?: string;
    startDate: Date;
    endDate: Date;
  };
}

interface MetricsDetailedTableProps {
  metrics: MetricDetails[];
}

export function MetricsDetailedTable({ metrics }: MetricsDetailedTableProps) {
  return (
    <Accordion type="multiple" className="space-y-4">
      {metrics.map((metric, index) => (
        <MetricDetailedItem key={index} metric={metric} />
      ))}
    </Accordion>
  );
}

function MetricDetailedItem({ metric }: { metric: MetricDetails }) {
  const { data, isLoading } = useMetricsDetails(metric.metricType, metric.filters);

  const getTrendIcon = () => {
    if (metric.trend === 'up') return <TrendingUp className="h-4 w-4" />;
    if (metric.trend === 'down') return <TrendingDown className="h-4 w-4" />;
    return <Minus className="h-4 w-4" />;
  };

  const getTrendColor = () => {
    if (metric.metricType === 'churn') {
      if (metric.trend === 'down') return 'text-success';
      if (metric.trend === 'up') return 'text-destructive';
    } else {
      if (metric.trend === 'up') return 'text-success';
      if (metric.trend === 'down') return 'text-destructive';
    }
    return 'text-muted-foreground';
  };

  return (
    <AccordionItem value={metric.title} className="border rounded-lg px-4">
      <AccordionTrigger className="hover:no-underline">
        <div className="flex items-center justify-between w-full pr-4">
          <div className="flex items-center gap-4">
            <span className="font-semibold">{metric.title}</span>
            <span className="text-2xl font-bold">{metric.value}</span>
          </div>
          {metric.change !== undefined && (
            <div className={`flex items-center gap-1 text-sm font-medium ${getTrendColor()}`}>
              {getTrendIcon()}
              <span>{metric.change > 0 ? '+' : ''}{metric.change.toFixed(1)}%</span>
            </div>
          )}
        </div>
      </AccordionTrigger>
      <AccordionContent className="pt-4">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : data ? (
          <MetricDetailTable
            metricType={metric.metricType}
            breakdown={data.breakdown}
            total={data.total}
            average={data.average}
          />
        ) : (
          <p className="text-sm text-muted-foreground text-center py-4">
            Nenhum dado disponível
          </p>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}
